import React, { useState, useEffect } from 'react';
import api from '../lib/api';
import toast from 'react-hot-toast';
import { Car, Plus, Edit2, Trash2, X } from 'lucide-react';

const Carros = () => {
  const [carros, setCarros] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCarro, setEditingCarro] = useState(null);
  const [formData, setFormData] = useState({ placa: '', modelo: '', ativo: true });

  useEffect(() => {
    loadCarros();
  }, []);

  const loadCarros = async () => {
    try {
      const response = await api.get('/carros');
      setCarros(response.data);
    } catch (error) {
      toast.error('Erro ao carregar carros');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingCarro) {
        await api.put(`/carros/${editingCarro.id}`, { ...editingCarro, ...formData });
        toast.success('Carro atualizado!');
      } else {
        await api.post('/carros', formData);
        toast.success('Carro cadastrado!');
      }
      setShowModal(false);
      setFormData({ placa: '', modelo: '', ativo: true });
      setEditingCarro(null);
      loadCarros();
    } catch (error) {
      toast.error('Erro ao salvar carro');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Deseja realmente excluir este carro?')) return;
    try {
      await api.delete(`/carros/${id}`);
      toast.success('Carro excluído!');
      loadCarros();
    } catch (error) {
      toast.error('Erro ao excluir carro');
    }
  };

  const openEdit = (carro) => {
    setEditingCarro(carro);
    setFormData({
      placa: carro.placa,
      modelo: carro.modelo,
      ativo: carro.ativo,
    });
    setShowModal(true);
  };

  if (loading) {
    return <div className="flex justify-center py-12"><div className="w-12 h-12 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin"></div></div>;
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="carros-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-600 flex items-center justify-center">
            <Car className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Carros</h1>
            <p className="text-slate-600">Gerencie a frota de veículos</p>
          </div>
        </div>
        <button onClick={() => { setShowModal(true); setFormData({ placa: '', modelo: '', ativo: true }); setEditingCarro(null); }} className="bg-gradient-to-r from-emerald-500 to-teal-600 text-white px-6 py-3 rounded-xl font-medium hover:shadow-lg transition-all flex items-center gap-2" data-testid="add-carro-btn">
          <Plus className="w-5 h-5" /> Adicionar Carro
        </button>
      </div>

      <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
        <table className="w-full">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="text-left py-4 px-6 text-sm font-medium text-slate-600">Placa</th>
              <th className="text-left py-4 px-6 text-sm font-medium text-slate-600">Modelo</th>
              <th className="text-left py-4 px-6 text-sm font-medium text-slate-600">Status</th>
              <th className="text-right py-4 px-6 text-sm font-medium text-slate-600">Ações</th>
            </tr>
          </thead>
          <tbody>
            {carros.map((carro) => (
              <tr key={carro.id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                <td className="py-4 px-6 text-sm font-medium text-slate-900">{carro.placa}</td>
                <td className="py-4 px-6 text-sm text-slate-600">{carro.modelo}</td>
                <td className="py-4 px-6">
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${carro.ativo ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>
                    {carro.ativo ? 'Ativo' : 'Inativo'}
                  </span>
                </td>
                <td className="py-4 px-6 text-right">
                  <button onClick={() => openEdit(carro)} className="text-blue-600 hover:text-blue-700 p-2 rounded-lg hover:bg-blue-50 transition-colors" data-testid={`edit-carro-${carro.id}`}>
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button onClick={() => handleDelete(carro.id)} className="text-red-600 hover:text-red-700 p-2 rounded-lg hover:bg-red-50 transition-colors ml-2" data-testid={`delete-carro-${carro.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {carros.length === 0 && (
          <div className="text-center py-12 text-slate-500">Nenhum carro cadastrado</div>
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-slate-900">{editingCarro ? 'Editar Carro' : 'Novo Carro'}</h2>
              <button onClick={() => setShowModal(false)} className="p-2 hover:bg-slate-100 rounded-lg transition-colors"><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Placa</label>
                <input type="text" value={formData.placa} onChange={(e) => setFormData({ ...formData, placa: e.target.value })} className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none" placeholder="ABC-1234" required data-testid="placa-input" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Modelo</label>
                <input type="text" value={formData.modelo} onChange={(e) => setFormData({ ...formData, modelo: e.target.value })} className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none" placeholder="Ex: Fiat Strada" data-testid="modelo-input" />
              </div>
              <div className="flex items-center gap-2">
                <input type="checkbox" checked={formData.ativo} onChange={(e) => setFormData({ ...formData, ativo: e.target.checked })} className="w-4 h-4 text-emerald-600 rounded" data-testid="ativo-checkbox" />
                <label className="text-sm text-slate-700">Ativo</label>
              </div>
              <div className="flex gap-3 pt-4">
                <button type="button" onClick={() => setShowModal(false)} className="flex-1 px-4 py-3 rounded-xl border border-slate-200 text-slate-600 font-medium hover:bg-slate-50 transition-colors">Cancelar</button>
                <button type="submit" className="flex-1 px-4 py-3 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-600 text-white font-medium hover:shadow-lg transition-all" data-testid="submit-carro-btn">Salvar</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Carros;
